// frontend/src/services/payrollService.js
import api from './api';

/**
 * Service for managing payroll
 */
const payrollService = {
  /**
   * Get all payroll records with filters
   * @param {Object} params - Query parameters
   * @returns {Promise} Response with payroll data
   */
  async getPayrollRecords(params = {}) {
    try {
      const response = await api.get('/admin/payroll', { params });
      return response.data;
    } catch (error) {
      console.error('Get payroll records error:', error);
      throw error;
    }
  },

  /**
   * Get payroll summary statistics
   * @param {Object} params - Query parameters (year, month)
   * @returns {Promise} Response with summary data
   */
  async getSummary(params = {}) {
    try {
      const response = await api.get('/admin/payroll/summary', { params });
      return response.data;
    } catch (error) {
      console.error('Get payroll summary error:', error);
      throw error;
    }
  },

  /**
   * Get a single payroll record with details
   * @param {number} id - Payroll record ID
   * @returns {Promise} Response with payroll details
   */
  async getPayrollRecord(id) {
    try {
      const response = await api.get(`/admin/payroll/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get payroll record error:', error);
      throw error;
    }
  },

  /**
   * Generate payroll for a specific month
   * @param {Object} data - Generation data (year, month, employee_id optional)
   * @returns {Promise} Response
   */
  async generatePayroll(data) {
    try {
      const response = await api.post('/admin/payroll/generate', data, { timeout: 120000 });
      return response.data;
    } catch (error) {
      console.error('Generate payroll error:', error);
      throw error;
    }
  },

  /**
   * Finalize a payroll record
   * @param {number} id - Payroll record ID
   * @returns {Promise} Response
   */
  async finalizePayroll(id) {
    try {
      const response = await api.put(`/admin/payroll/${id}/finalize`);
      return response.data;
    } catch (error) {
      console.error('Finalize payroll error:', error);
      throw error;
    }
  },

  /**
   * Mark a payroll record as paid
   * @param {number} id - Payroll record ID
   * @returns {Promise} Response
   */
  async markAsPaid(id) {
    try {
      const response = await api.put(`/admin/payroll/${id}/mark-paid`);
      return response.data;
    } catch (error) {
      console.error('Mark as paid error:', error);
      throw error;
    }
  },

  /**
   * Get rich payroll details including overtime breakdown, leave balances, and audit log
   * @param {number} id - Payroll record ID
   * @returns {Promise} Response with full payroll details
   */
  async getPayrollDetails(id) {
    try {
      const response = await api.get(`/admin/payroll/${id}/details`);
      return response.data;
    } catch (error) {
      console.error('Get payroll details error:', error);
      throw error;
    }
  },

  /**
   * Set a manual adjustment on a payroll record (draft or finalized only)
   * @param {number} id - Payroll record ID
   * @param {number} amount - Adjustment amount (can be negative)
   * @param {string} reason - Non-empty reason for the adjustment
   * @returns {Promise} Response with updated payroll record
   */
  async updateManualAdjustment(id, amount, reason) {
    try {
      const response = await api.patch(`/admin/payroll/${id}/adjustment`, { amount, reason });
      return response.data;
    } catch (error) {
      console.error('Update manual adjustment error:', error);
      throw error;
    }
  },

  /**
   * Delete a draft payroll record
   * @param {number} id - Payroll record ID
   * @returns {Promise} Response
   */
  async deletePayroll(id) {
    try {
      const response = await api.delete(`/admin/payroll/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete payroll error:', error);
      throw error;
    }
  },

  /** Status of every month of a year: { year, months: [{ month, state, total, draft, finalized, paid, total_final_salary }] } */
  async getOverview(year) {
    const response = await api.get('/admin/payroll/overview', { params: { year } });
    return response.data;
  },

  /** Finalize every draft of a month */
  async bulkFinalize(year, month) {
    const response = await api.post('/admin/payroll/bulk/finalize', { year, month });
    return response.data;
  },

  /** Mark every finalized record of a month as paid */
  async bulkMarkPaid(year, month) {
    const response = await api.post('/admin/payroll/bulk/mark-paid', { year, month });
    return response.data;
  }
};

export default payrollService;
